/**
 * Utility functions for capacity and utilization calculations
 */
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';

/**
 * Calculates utilization percentage based on capacity per day
 * @param capacityPerDay - Team member's capacity per day in hours
 * @param standardWorkday - Standard workday hours (default: 8)
 * @returns Utilization percentage rounded to nearest integer
 */
export function calculateUtilizationPercentage(
  capacityPerDay: number,
  standardWorkday: number = 8
): number {
  return Math.round((capacityPerDay / standardWorkday) * 100);
}

/**
 * Calculates billable capacity utilization percentage
 * This represents what percentage of total working time is billable
 * @param billableCapacityPercent - Billable capacity percentage (0-100)
 * @returns Billable capacity percentage (same as input, since it's already a percentage)
 */
export function calculateBillableUtilizationPercentage(billableCapacityPercent: number): number {
  return billableCapacityPercent || 0;
}

/**
 * Calculates billable working hours per day
 * @param workingMinutesPerDay - Total working minutes per day
 * @param billableCapacityPercent - Billable capacity percentage (0-100)
 * @returns Billable working hours per day
 */
export function calculateBillableWorkingHours(
  workingMinutesPerDay: number,
  billableCapacityPercent: number
): number {
  const workingHoursPerDay = workingMinutesPerDay / 60;
  return workingHoursPerDay * (billableCapacityPercent / 100);
}

/**
 * Gets the appropriate color class for utilization percentage
 * @param utilizationPercentage - Utilization percentage
 * @returns Tailwind CSS color class
 */
export function getUtilizationColor(utilizationPercentage: number): string {
  if (utilizationPercentage < 26) return DEFAULT_COLOR_PALETTE.red;
  if (utilizationPercentage >= 26 && utilizationPercentage < 46)
    return DEFAULT_COLOR_PALETTE.yellow;
  if (utilizationPercentage >= 46 && utilizationPercentage < 71) return DEFAULT_COLOR_PALETTE.amber;
  return DEFAULT_COLOR_PALETTE.green;
}
